'use client';

import { Card } from '@app/app/_components/Card/Card';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { EditIcon } from '@app/app/_icons/EditIcon';
import { InsuredPerson } from '@app/domain/model/eshop/personal-accidents/InsuredPerson';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { useTranslations } from 'next-intl';
import { InsuredPersonCard } from '@app/app/esales/personal-accidents/_sections/components/InsuredPersonCard';
import { PolicyholderCard } from '@app/app/esales/personal-accidents/_sections/components/PolicyholderCard';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { ChangeEvent, useMemo } from 'react';

interface Props {
    insuredPersons: InsuredPerson[];
    handleNameChange: (id: string) => (e: ChangeEvent<HTMLInputElement>) => void;
    handleSurnameChange: (id: string) => (e: ChangeEvent<HTMLInputElement>) => void;
    handlePersonalCodeChange: (id: string) => (e: ChangeEvent<HTMLInputElement>) => void;
    violations?: ViolationMap;
    policyholderIsInsuredPerson: boolean;
    onChangeNumberOfPersons: () => void;
    formState: FormState<PurchasePersonalInsuranceResult>;
    editPolicyholderInfo: () => void;
    changeAgeOfPerson: () => void;
}

interface PersonError {
    name?: ViolationError;
    surname?: ViolationError;
    personalCode?: ViolationError;
    ageMismatch?: boolean;
    duplicatePersonalCode?: boolean;
}

export function InsuredPeopleStep({
    insuredPersons,
    handleNameChange,
    handleSurnameChange,
    handlePersonalCodeChange,
    violations,
    policyholderIsInsuredPerson,
    onChangeNumberOfPersons,
    formState,
    editPolicyholderInfo,
    changeAgeOfPerson,
}: Props) {
    const t = useTranslations();

    const personErrors = useMemo(() => {
        const insuredPersonsViolations = violations?.insuredPersons;

        const personalCodeCounts = new Map<string, number>();
        insuredPersons.forEach(person => {
            if (person.personalCode && person.personalCode.trim() !== '') {
                const count = personalCodeCounts.get(person.personalCode) || 0;
                personalCodeCounts.set(person.personalCode, count + 1);
            }
        });

        if (!insuredPersonsViolations || isViolationMessage(insuredPersonsViolations)) {
            return insuredPersons.map((person): PersonError => {
                const errors: PersonError = {};

                if (person.personalCode && person.personalCode.trim() !== '') {
                    if (personalCodeCounts.get(person.personalCode)! > 1) {
                        errors.duplicatePersonalCode = true;
                    }

                    const parsedData = parseLithuanianPersonalCode(person.personalCode);
                    if (parsedData.isValid && parsedData.age !== undefined && parsedData.age !== person.age) {
                        errors.ageMismatch = true;
                    }
                }

                return errors;
            });
        }

        return insuredPersons.map((person, index): PersonError => {
            const personViolations = insuredPersonsViolations[index];
            const errors: PersonError = {};

            if (personViolations && !isViolationMessage(personViolations)) {
                errors.name = personViolations.name;
                errors.surname = personViolations.surname;
                errors.personalCode = personViolations.personalCode;
            }

            if (person.personalCode && person.personalCode.trim() !== '') {
                if (personalCodeCounts.get(person.personalCode)! > 1) {
                    errors.duplicatePersonalCode = true;
                }

                const parsedData = parseLithuanianPersonalCode(person.personalCode);
                if (parsedData.isValid && parsedData.age !== undefined && parsedData.age !== person.age) {
                    errors.ageMismatch = true;
                }
            }

            return errors;
        });
    }, [violations, insuredPersons]);

    const policyholderPerson = insuredPersons.find(person => person.isPolicyholder);
    const additionalPersons = insuredPersons.filter(person => !person.isPolicyholder);

    return (
        <div className="flex flex-col gap-4">
            {!policyholderIsInsuredPerson && (
                <div className="flex flex-col gap-4">
                    <Heading level="h3" className="text-xl">
                        {t('eshop.personalAccidents.policyholder')}
                    </Heading>
                    <PolicyholderCard
                        name={formState.data?.formData.policyholderFirstName || ''}
                        surname={formState.data?.formData.policyholderLastName || ''}
                        personalCode={formState.data?.formData.policyholderPersonalCode || ''}
                        age={
                            parseLithuanianPersonalCode(formState.data?.formData.policyholderPersonalCode || '').age ??
                            null
                        }
                        edit={editPolicyholderInfo}
                        changeAge={changeAgeOfPerson}
                    />
                </div>
            )}

            {policyholderPerson && policyholderIsInsuredPerson && (
                <div className="flex flex-col gap-4">
                    <PolicyholderCard
                        name={policyholderPerson.name || ''}
                        surname={policyholderPerson.surname || ''}
                        personalCode={policyholderPerson.personalCode || ''}
                        age={policyholderPerson.age}
                        index={insuredPersons.indexOf(policyholderPerson)}
                        edit={editPolicyholderInfo}
                        ageMismatch={
                            policyholderPerson.personalCode
                                ? policyholderPerson.age !==
                                  parseLithuanianPersonalCode(policyholderPerson.personalCode).age
                                : false
                        }
                        changeAge={changeAgeOfPerson}
                    />
                </div>
            )}

            {additionalPersons.length > 0 && (
                <div className="flex flex-col gap-4">
                    {!policyholderIsInsuredPerson && (
                        <Heading level="h3" className="text-xl">
                            {t('eshop.personalAccidents.insuredPeople')}
                        </Heading>
                    )}

                    {additionalPersons.map(person => {
                        const personIndex = insuredPersons.indexOf(person);
                        return (
                            <InsuredPersonCard
                                key={person.id}
                                age={person.age}
                                name={person.name || ''}
                                surname={person.surname || ''}
                                personalCode={person.personalCode || ''}
                                id={person.id}
                                index={personIndex}
                                isPrimary={false}
                                handleNameChange={handleNameChange}
                                handleSurnameChange={handleSurnameChange}
                                handlePersonalCodeChange={handlePersonalCodeChange}
                                errors={personErrors[personIndex]}
                                ageMismatch={personErrors[personIndex]?.ageMismatch}
                                changeAge={changeAgeOfPerson}
                            />
                        );
                    })}
                </div>
            )}

            <GhostButton onClick={onChangeNumberOfPersons}>
                <Card className="flex w-full flex-row items-center justify-center gap-2 p-6 text-primary-500 hover:bg-primary-100">
                    <Paragraph isBold className="text-primary-500">
                        {t('eshop.personalAccidents.changeNumberOfInsuredPersons')}
                    </Paragraph>
                    <EditIcon className="size-6" />
                </Card>
            </GhostButton>
        </div>
    );
}
